import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Ocupacion } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { OcupacionForm } from "../OcupacionForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListOcupaciones.scss";

const ocupacionController = new Ocupacion();

export function ListOcupaciones({ active, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const [items,       setItems]       = useState(null);
  const [filtered,    setFiltered]    = useState([]);
  const [search,      setSearch]      = useState("");
  const [showModal,   setShowModal]   = useState(false);
  const [selected,    setSelected]    = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMsg,  setConfirmMsg]  = useState("");
  const [isDelete,    setIsDelete]    = useState(false);
  const [sortAsc,     setSortAsc]     = useState(true);
  const [rowMenu,     setRowMenu]     = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".ocupaciones-list__row-menu, .ocupaciones-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setItems(null);
        const res = await ocupacionController.getOcupaciones(accessToken, active);
        setItems(res);
        setFiltered(res);
      } catch {
        setItems([]);
        setFiltered([]);
      }
    })();
  }, [active, reload]);

  useEffect(() => {
    if (!items) return;
    if (!search) { setFiltered(items); return; }
    const q = search.toLowerCase();
    setFiltered(items.filter((o) => o.code?.toLowerCase().includes(q) || o.name?.toLowerCase().includes(q)));
  }, [search, items]);

  const openCreate = () => { setSelected(null); setShowModal(true); };
  const openEdit   = (o)  => { setSelected(o);   setShowModal(true); };

  const openToggle = (o) => {
    setSelected(o);
    setIsDelete(false);
    setConfirmMsg(`¿${o.active ? t("deactivate") : t("activate")} "${o.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (o) => {
    setSelected(o);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${o.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await ocupacionController.updateOcupacion(accessToken, selected._id, { active: !selected.active });
      toast.success(selected.active ? t("deactivated") : t("activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await ocupacionController.deleteOcupacion(accessToken, selected._id);
      toast.success(t("deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (items === null) return <LoadingSpinner />;

  return (
    <div className="ocupaciones-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="ocupaciones-list__card">
        <div className="ocupaciones-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-ocupaciones-btn" onClick={openCreate} title={t("create")}>
            <Icon name="plus" />
          </Button>
        </div>

        {filtered.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="ocupaciones-list__tbl-scroll">
            <Table className="ocupaciones-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell
                    sorted={sortAsc ? "ascending" : "descending"}
                    onClick={() => {
                      const asc = !sortAsc;
                      setSortAsc(asc);
                      setFiltered((prev) =>
                        [...prev].sort((a, b) =>
                          asc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)
                        )
                      );
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {t("occupation")}
                  </Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.map((o) => (
                  <Table.Row key={o._id}>
                    <Table.Cell data-label={t("code")}>{o.code || "—"}</Table.Cell>
                    <Table.Cell data-label={t("occupation")}>{o.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="ocupaciones-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="ocupaciones-list__row-menu">
                        <button type="button" className="ocupaciones-list__row-menu-btn" onClick={(e) => toggleRowMenu(o._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === o._id && ReactDOM.createPortal(
                        <div
                          className="ocupaciones-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(o); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(o); setRowMenu(null); }}>
                            <Icon name={o.active ? "ban" : "check"} />{o.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="ocupaciones-list__row-menu-item--danger" onClick={() => { openDelete(o); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selected ? `${t("edit")}: ${selected.name}` : t("new_occupation")}
        size="small"
        className="occupation-modal"
      >
        <OcupacionForm
          ocupacion={selected}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? t("delete") : (selected?.active ? t("deactivate") : t("activate"))}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel={t("accept")}
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
